import { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import TablePagination from '@mui/material/TablePagination';
import LuigiClient from '@luigi-project/client';
import appTheme from '../../../AppThemes';
import getStateNameFromAbbr from './StoreConfig';
import './StorePickerSettings.css';

const StorePickerSettings = props => {
  const [stores, setStores] = useState(props.stores);
  const storeAmount = Number(props.stores.length);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(storeAmount);
  const [rows, setRowsPerPage] = useState(5);
  const [searchString, setSearchString] = useState('');
  const [clearButton, showHideClear] = useState('none');
  const [selectedStore, storeSelected] = useState({
    storeName: props.currentStore.storeName,
    storeNumber: props.currentStore.storeNumber,
  });
  //Events
  const handleChangePage = (event, newPage) => {
    event.preventDefault();
    setPage(newPage);
  };
  const handleChangeRowsPerPage = event => {
    event.preventDefault();
    setRowsPerPage(parseInt(event.target.value, 5));
    setPage(0);
  };

  const handleStoreChange = event => {
    event.preventDefault();
    setSearchString(event.target.value);
    const filteredStores = filterStores(props.stores, event.target.value);
    const newCount = filteredStores.length;
    setPage(0);
    setStores(filteredStores);
    setCount(newCount);
    showHideClear('block');
    if (event.target.value.length === 0) {
      showHideClear('none');
      setStores(props.stores);
      const countNum = props.stores.length;
      setCount(countNum);
    }
  };

  //Filter
  const filterStores = (storeList, filter) => {
    const filteredStores = storeList.slice().filter(item => {
      item.searchIndex = undefined;
      item.searchResult = undefined;
      item.searchWeight = undefined;

      const addressStr = (
        (item.address ? item.address : '') +
        (item.city ? item.city : '') +
        (item.state ? getStateNameFromAbbr(item.state) + item.state : '') +
        (item.zipCode ? item.zipCode : '') +
        (item.country ? item.country : '')
      ).toLowerCase();
      const statusStr = (
        item.locationStatus ? item.locationStatus : ''
      ).toLowerCase();

      const filterStr = filter.toLowerCase();
      if (item.storeName.toLowerCase().indexOf(filterStr) !== -1) {
        item.searchWeight = 2;
        item.searchIndex = item.storeName.toLowerCase().indexOf(filterStr);
        return true;
      } else if (item.storeNumber.toLowerCase().indexOf(filterStr) !== -1) {
        item.searchWeight = 2;
        item.searchIndex = item.storeNumber.toLowerCase().indexOf(filterStr);
        return true;
      } else if (addressStr.indexOf(filterStr) !== -1) {
        item.searchWeight = 1;
        item.searchResult =
          `Address: ${item.address ? item.address : ''}, ` +
          `${item.city ? item.city : ''}, ` +
          `${item.state ? item.state : ''} ` +
          `${item.zipCode ? item.zipCode : ''} ` +
          `${item.country ? item.country : ''}`;
        return true;
      } else if (statusStr.indexOf(filterStr) !== -1) {
        item.searchWeight = 1;
        item.searchResult = `Status: ${item.locationStatus}`;
        return true;
      } else {
        return false;
      }
    });

    return filteredStores.sort((a, b) => {
      return sortBySearchWeight(a, b);
    });
  };
  const sortBySearchWeight = (a, b) => {
    if (a.searchWeight > b.searchWeight) {
      return -1;
    } else if (b.searchWeight > a.searchWeight) {
      return 1;
    } else {
      return sortBySearchIndex(a, b);
    }
  };

  const sortBySearchIndex = (a, b) => {
    if (a.searchIndex < b.searchIndex) {
      return -1;
    } else if (b.searchIndex > a.searchIndex) {
      return 1;
    } else {
      return sortByNumber(a, b);
    }
  };
  const sortByNumber = (a, b) => {
    const aNumber = a.storeNumber.replace('T', '10').replace('D', '20');
    const bNumber = b.storeNumber.replace('T', '10').replace('D', '20');
    return aNumber.localeCompare(bNumber);
  };
  const renderNoStores = () => {
    const noResults =
      stores.length === 0 ? (
        <p className="cfa-text-center">No matching stores to display.</p>
      ) : (
        ''
      );
    return noResults;
  };

  const handleClear = event => {
    event.preventDefault();
    showHideClear('none');
    setSearchString('');
    setStores(props.stores);
    const countnum = props.stores.length;
    setCount(countnum);
  };
  const controlPointSend = (msgType, payload) => {
    LuigiClient.setTargetOrigin(window.location.origin);

    LuigiClient.sendCustomMessage({
      id: 'controlpoint.send',
      value: {
        id: msgType,
        value: payload,
      },
    });
  };
  const storeSelect = (storeNumber, storeName) => {
    controlPointSend('store.change', storeNumber);
    storeSelected({ storeName: storeName, storeNumber: storeNumber });
    props.onSelectStore({ storeName: storeName, storeNumber: storeNumber });
    localStorage.setItem('store', selectedStore);
  };

  const shouldShowAddress = (address, zipCode) => {
    if (address !== undefined) {
      const address1 = address.toLowerCase();
      const zipCode1 = zipCode.toLowerCase();
      if (
        address1.includes(searchString.toLowerCase()) &&
        searchString.length > 3
      ) {
        return true;
      } else if (
        zipCode1.includes(searchString.toLowerCase()) &&
        searchString.length > 3
      ) {
        return true;
      } else {
        return false;
      }
    }
  };
  //Highlight Functionality

  const checkForHighlightHandler = strToCompare => {
    if (strToCompare && searchString.length > 0) {
      const filter = searchString.toLowerCase();
      const index = strToCompare.toLowerCase().indexOf(filter);
      if (index !== -1) {
        const start = strToCompare.substring(0, index);
        const matchingPart = strToCompare.substr(index, filter.length);
        const end = strToCompare.substr(index + filter.length);
        return (
          <span>
            {start}
            <b
              style={{
                color: appTheme.theme.palette.primary.main,
              }}
            >
              {matchingPart}
            </b>
            {end}
          </span>
        );
      } else {
        return strToCompare;
      }
    } else {
      return strToCompare;
    }
  };

  return (
    <>
      <div
        id="store-picker-container"
        className="justify-content-center mt-3 mb-2"
      >
        <Box
          sx={{
            '& > :not(style)': { m: 1 },
          }}
          noValidate
          autoComplete="off"
        >
          <div className="col store-cont">
            <div className="mb-2">
              <div className="col cont">
                <TextField
                  focused
                  autoComplete="off"
                  onSubmit={handleStoreChange}
                  id="filled-basic"
                  className="store-search Mui-focused"
                  variant="filled"
                  value={searchString}
                  onChange={handleStoreChange}
                  placeholder={`Search ${count} Stores`}
                  InputProps={{
                    endAdornment: (
                      <span
                        onClick={handleClear}
                        style={{
                          color: appTheme.theme.palette.primary.main,
                          display: clearButton,
                        }}
                        className="clear"
                      >
                        clear
                      </span>
                    ),
                  }}
                />
                <section id="store-list">
                  {stores.slice(page * 5, page * 5 + 5).map((store, i) => {
                    return (
                      <section
                        id="store-item"
                        key={store.storeNumber}
                        style={{
                          color:
                            i === 0
                              ? `${appTheme.theme.palette.primary.main}`
                              : '',
                        }}
                        onClick={() =>
                          storeSelect(store.storeNumber, store.storeName)
                        }
                      >
                        <div
                          className="row cfa-18 cfa-store-select clicky pt-2 pb-2"
                          key={store.storeNumber}
                          id={`storeSelect-store-${store.storeNumber}`}
                        >
                          <div className="col-4 text-left">
                            {checkForHighlightHandler(store.storeNumber)}
                          </div>
                          <div className="col text-left ellipse-overflow pl-0">
                            {checkForHighlightHandler(store.storeName)}
                          </div>
                          {shouldShowAddress(store?.address, store?.zipCode) ? (
                            <span className="cfa-search-result">
                              <span>Address: </span>
                              <span>
                                {checkForHighlightHandler(store?.address)},{' '}
                              </span>
                              <span>
                                {checkForHighlightHandler(store?.city)},{' '}
                              </span>
                              <span>
                                {checkForHighlightHandler(store?.state)},{' '}
                              </span>
                              <span>
                                {checkForHighlightHandler(store?.zipCode)},{' '}
                              </span>
                            </span>
                          ) : (
                            ''
                          )}
                        </div>
                      </section>
                    );
                  })}
                </section>
              </div>
            </div>

            {renderNoStores()}
            <TablePagination
              id="store-pagination"
              component="div"
              count={count}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rows}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[]}
            />
          </div>
        </Box>
      </div>
    </>
  );
};

export default StorePickerSettings;
